import { useCallback, useMemo } from 'react'

import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { useDocumentTitle } from '@/utility/useDocumentTitle'
import {
    MidmarkFormikTextField,
    MidmarkButton,
    MidmarkLink,
} from '@midmark-enterprise/shared-components'
import { FormLayout } from '@/components/Shared/FormLayout'
import { UserVerificationService } from '@/services/UserVerificationService/UserVerificationService'
import { useSimplePager } from '@/components/Shared/SimplePager'
import { useTranslation } from 'react-i18next'
import { useReCaptchaV3 } from '@/hooks/useReCaptchaV3'

type PasswordResetFormProps = {
    redirectUri: string
    email: string
    updateEmailVerification: (
        identificationGuid: string,
        emailAddress: string,
    ) => void
}

export function buildSchema(t: (key: string) => string) {
    return Yup.object().shape({
        emailAddress: Yup.string()
            .email(t('password.passwordResetForm.invalidEmail'))
            .required(t('password.passwordResetForm.emailRequired'))
            .matches(
                /^(?!.*@midmark.com).*$/,
                t('password.passwordResetForm.emailRestriction'),
            ),
    })
}

function getSessionStorageItem(key: string): string | null {
    try {
        return sessionStorage.getItem(key)
    } catch {
        return null
    }
}

export default function PasswordResetForm({
    redirectUri,
    updateEmailVerification,
    email,
}: PasswordResetFormProps) {
    const { t } = useTranslation()
    const simplePager = useSimplePager()
    const sendVerificationEmailMutation =
        UserVerificationService.useSendVerificationEmailMutation()
    useDocumentTitle(t('password.passwordResetForm.pageTitle'))

    const { isReady: isRecaptchaV3Ready, execute: executeRecaptchaV3 } =
        useReCaptchaV3({
            siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY_V3,
        })

    const validationSchema = useMemo(() => {
        return buildSchema(t)
    }, [t])

    const handleSubmit = useCallback(
        async (
            values: { emailAddress: string },
            formikHelpers: FormikHelpers<{
                emailAddress: string
            }>,
        ) => {
            try {
                const recaptchaTestKey =
                    getSessionStorageItem('recaptchaTestKey') ?? undefined
                let recaptchaToken: string | undefined

                if (!recaptchaTestKey && !isRecaptchaV3Ready) {
                    formikHelpers.setSubmitting(false)
                    return
                }

                if (!recaptchaTestKey && isRecaptchaV3Ready) {
                    recaptchaToken =
                        await executeRecaptchaV3('password_reset')
                }

                const data =
                    await sendVerificationEmailMutation.mutateAsync({
                        emailAddress: values.emailAddress,
                        isNewUserRegistration: false,
                        recaptchaToken,
                        recaptchaTestKey,
                    })
                updateEmailVerification(
                    data.identificationGuid,
                    values.emailAddress,
                )
                simplePager.nextPage()
            } catch (error) {
                console.log(error)
            }
        },
        [
            sendVerificationEmailMutation,
            simplePager,
            updateEmailVerification,
            isRecaptchaV3Ready,
            executeRecaptchaV3,
        ],
    )

    return (
        <Formik
            initialValues={{
                emailAddress: email,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {(props) => (
                <FormLayout
                    title={t('password.passwordResetForm.title')}
                    description={t('password.passwordResetForm.description')}
                    buttons={
                        <>
                            {redirectUri && redirectUri.length > 0 && (
                                <MidmarkLink
                                    variant="body1"
                                    component="a"
                                    href={redirectUri}
                                    data-testid={
                                        DATA_TESTIDS.iKnowMyPasswordLink
                                    }
                                >
                                    {t('password.passwordResetForm.iKnowMyPasswordLink')}
                                </MidmarkLink>
                            )}
                            <MidmarkButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                data-testid={DATA_TESTIDS.continueButton}
                                isSubmitting={props.isSubmitting}
                            >
                                {t('password.passwordResetForm.continueButton')}
                            </MidmarkButton>
                        </>
                    }
                >
                    <MidmarkFormikTextField
                        label={t('password.passwordResetForm.emailLabel')}
                        autoComplete="email"
                        data-testid={DATA_TESTIDS.emailTextField}
                        placeholder={t('password.passwordResetForm.emailPlaceholder')}
                        inputProps={{
                            'data-testid': DATA_TESTIDS.emailTextFieldInput,
                        }}
                        formikProps={props}
                        name="emailAddress"
                        fullWidth={true}
                    />

                </FormLayout>
            )}
        </Formik>
    )
}

export const DATA_TESTIDS = {
    continueButton: 'continue-button',
    iKnowMyPasswordLink: 'i-know-my-password-link',
    signInUsingEmailLink: 'sign-in-using-email-link',
    emailTextField: 'email-text-field',
    emailTextFieldInput: 'email-text-field-input',
}
